import React, { createContext, useCallback, useContext, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ExclamationCircleIcon,
  InformationCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

type ToastType = 'success' | 'error' | 'info' | 'warning';

interface Toast {
  id: string;
  type: ToastType;
  message: string;
}

interface ToastContextValue {
  toast: {
    success: (message: string) => void;
    error: (message: string) => void;
    info: (message: string) => void;
    warning: (message: string) => void;
  };
}

const ToastContext = createContext<ToastContextValue | null>(null);

let toastId = 0;

const ICONS: Record<ToastType, React.ReactNode> = {
  success: <CheckCircleIcon className="w-5 h-5 flex-shrink-0" />,
  error: <ExclamationCircleIcon className="w-5 h-5 flex-shrink-0" />,
  info: <InformationCircleIcon className="w-5 h-5 flex-shrink-0" />,
  warning: <ExclamationTriangleIcon className="w-5 h-5 flex-shrink-0" />,
};

const STYLES: Record<ToastType, { border: string; text: string }> = {
  success: { border: 'border-l-emerald-500', text: 'text-emerald-400' },
  error: { border: 'border-l-red-500', text: 'text-red-400' },
  info: { border: 'border-l-orange-500', text: 'text-orange-400' },
  warning: { border: 'border-l-yellow-500', text: 'text-yellow-400' },
};

const DURATIONS: Record<ToastType, number> = {
  success: 5000,
  error: 8000,
  info: 5000,
  warning: 6000,
};

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const addToast = useCallback(
    (type: ToastType, message: string) => {
      const id = `toast-${++toastId}`;
      setToasts((prev) => [...prev, { id, type, message }]);
      setTimeout(() => removeToast(id), DURATIONS[type]);
    },
    [removeToast]
  );

  const toast = {
    success: (message: string) => addToast('success', message),
    error: (message: string) => addToast('error', message),
    info: (message: string) => addToast('info', message),
    warning: (message: string) => addToast('warning', message),
  };

  return (
    <ToastContext.Provider value={{ toast }}>
      {children}

      {/* Toast container — fixed at top center */}
      <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[9999] flex flex-col gap-2 w-full max-w-md px-4 pointer-events-none">
        <AnimatePresence>
          {toasts.map((t) => {
            const style = STYLES[t.type];
            return (
              <motion.div
                key={t.id}
                initial={{ opacity: 0, y: -20, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -10, scale: 0.95 }}
                transition={{ duration: 0.2 }}
                className={`pointer-events-auto glass-card-strong rounded-xl p-4 flex items-center justify-between border-l-2 ${style.border}`}
              >
                <div className={`flex items-center ${style.text} text-sm gap-2 min-w-0`}>
                  {ICONS[t.type]}
                  <span className="break-words">{t.message}</span>
                </div>
                <button
                  onClick={() => removeToast(t.id)}
                  className="text-white/30 hover:text-white/60 ml-3 flex-shrink-0 transition-colors"
                >
                  <XMarkIcon className="w-4 h-4" />
                </button>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>
    </ToastContext.Provider>
  );
};

export const useToast = (): ToastContextValue['toast'] => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within a ToastProvider');
  return ctx.toast;
};
